import { useForm } from "@/lib/form/form"
import { useFormContext } from "@/lib/form/form-context"
import {
  DropdownParamSchema,
  ParamSchema,
  ParamSchemaLimit,
  ParamSchemaMode,
  ParamSchemaType,
  ParamValidationType,
  RadioParamSchema,
} from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { createUuid, EnumsOf } from "@/lib/utils"
import { createChoiceValidator } from "@/lib/validators/choice"
import { createLimitValidator, createValidator } from "@/lib/validators/limit"
import {
  DepartmentSchema,
  PackagingMachineSchema,
  PackagingParamSchema,
  PackagingParamSchemaType,
  PackagingSKU,
  ParamListItem,
  ParamListItemPossibleValue,
  SKULimit,
} from "@/packaging/lib/schema"
import {
  createGeneralSectionValidator,
  createLeakageValidator,
  createLeakedPacketsValidator,
} from "@/packaging/lib/validators"
import { get, padStart } from "lodash"
import { useEffect } from "react"
import * as y from "yup"

export interface FormSchema {
  id: string
  name: string
  machines: MachineFormSchema[]
}

export interface MachineFormSchema {
  id: string
  name: string
  products: ProductSchema[]
}

export interface ProductSchema {
  id: string
  sections: ProductSectionSchema[]
}

export interface ProductSectionSchema {
  id: string
  name: string
  type: PackagingParamSchemaType
  parameters: PackagingParamWidgetSchema[]
}

export type PackagingParamWidgetSchema =
  | RadioParamSchema
  | DropdownParamSchema
  | ParamSchema

export function deptSchemaToFormSchema(schema: DepartmentSchema): FormSchema {
  const result: FormSchema = {
    id: schema.id,
    name: schema.name,
    machines: [],
  }

  for (const machine of schema.machines) {
    const builder = new MachineFormSchemaBuilder(machine)
    const product = builder.addProduct()
    builder.addSection(
      product.id,
      MarketType.Domestic,
      PackagingParamSchemaType.CERTIFICATION,
      false
    )

    result.machines.push(builder.schema)
  }

  return result
}

export const MarketType = {
  Domestic: "Domestic",
  Export: "Export",
} as const
export type MarketType = EnumsOf<typeof MarketType>

export class MachineFormSchemaBuilder {
  machineSchema: PackagingMachineSchema
  possibleProducts: ParamListItemPossibleValue[]
  schema: MachineFormSchema
  sku: string
  limits: Record<string, SKULimit[]>

  constructor(
    machineSchema: PackagingMachineSchema,
    initial?: MachineFormSchema
  ) {
    this.limits = {}
    this.sku = ""
    this.machineSchema = machineSchema

    this.schema = initial || {
      id: machineSchema.id,
      name: machineSchema.name,
      products: [],
    }

    this.possibleProducts =
      (this.machineSchema.parameters
        .find(
          (param) => param.type === PackagingParamSchemaType.GENERAL_INFORMATION
        )
        ?.list.find((item) => item.name === "Product")
        ?.possibleValues as ParamListItemPossibleValue[]) || []
  }

  private setLimits(skus: PackagingSKU[]) {
    for (const sku of skus) {
      this.limits[sku.id] = sku.limits
    }
  }

  setSKUs(productId: string, selectedProductId: string) {
    const product = this.schema.products.find(
      (product) => product.id === productId
    )

    if (!product) {
      return
    }

    const generalSection = product.sections.find(
      (section) => section.type === PackagingParamSchemaType.GENERAL_INFORMATION
    )

    if (!generalSection) {
      return
    }

    const skus =
      this.possibleProducts.find(
        (possible) => possible.id === selectedProductId
      )?.skus || []

    generalSection.parameters = generalSection.parameters.map((param) => {
      if (param.name === "SKU") {
        const dropdownParam = param as RadioParamSchema
        dropdownParam.options = skus.map((sku) => ({
          id: sku.id,
          label: sku.name,
          value: sku.id,
        }))
      }

      return param
    })

    this.setLimits(skus)
  }

  setCurrSKU(id: string) {
    this.sku = id
  }

  private getLimit(skuId: string, name: string): SKULimit | undefined {
    const limits = this.limits[skuId]
    if (!limits) {
      return
    }

    return limits.find((limit) => limit.name === name)
  }

  addSection(
    productId: string,
    market: MarketType,
    section: PackagingParamSchemaType,
    replace: boolean
  ) {
    const product = this.schema.products.find((item) => item.id === productId)
    if (!product) {
      return
    }

    const sectionSchema = this.machineSchema.parameters.find(
      (item) => item.type === section
    )
    if (!sectionSchema) {
      return
    }

    return this._addSection({
      product,
      market,
      sectionSchema,
      type: section,
      replace,
    })
  }

  private _addSection({
    product,
    sectionSchema,
    type,
    replace = false,
    market,
  }: AddSectionParams) {
    let sectionName = sectionSchema.name || ""

    if (type === PackagingParamSchemaType.LEAKAGE) {
      const next = product.sections.length - 1

      sectionName = `Leakage ${padStart(`${next}`, 2, "0")}`
    }

    const newSection: ProductSectionSchema = {
      id: createUuid(),
      type: type,
      name: sectionName,
      parameters: [],
    }

    for (const listItem of sectionSchema.list) {
      const forBothMarkets = !(listItem.labelDomestic && listItem.labelExport)
      const invalidItem =
        (market === MarketType.Domestic && !listItem.labelDomestic) ||
        (market === MarketType.Export && !listItem.labelExport)

      if (!forBothMarkets && invalidItem) {
        continue
      }

      const limit = this.getLimit(this.sku, listItem.name)
      let appliedLimit: ParamSchemaLimit | undefined

      if (limit) {
        appliedLimit = {
          min: limit.minLimit,
          max: limit.maxLimit,
          gradeRanges: [],
        }
      }

      if (listItem.validationType === ParamValidationType.LIMIT && !limit) {
        continue
      }

      let mode = listItem.mode
      let validationType = listItem.validationType || ParamValidationType.NONE
      if (listItem.name === "Total Leaked Packets") {
        mode = ParamSchemaMode.INFO
        validationType = listItem.validationType || ParamValidationType.NONE
      } else if (appliedLimit) {
        validationType = ParamValidationType.LIMIT
      } else {
        validationType = listItem.validationType || ParamValidationType.NONE
      }

      if (market === MarketType.Domestic) {
        const parameter: PackagingParamWidgetSchema = {
          id: listItem.id,
          name: listItem.name,
          mode: mode,
          mandatory: listItem.mandatory,
          type: listItem.inputTypeDomestic,
          validationType: validationType,
          unit: listItem.unitDomestic || "",
          limit: appliedLimit,
        }

        addOptionsToParam(parameter, listItem, MarketType.Domestic)

        newSection.parameters.push(parameter)
      } else {
        const parameter: PackagingParamWidgetSchema = {
          id: listItem.id,
          name: listItem.name,
          mode: mode,
          mandatory: listItem.mandatory,
          type: listItem.inputTypeExport,
          validationType: validationType,
          unit: listItem.unitExport || "",
          limit: appliedLimit,
        }

        addOptionsToParam(parameter, listItem, MarketType.Export)

        newSection.parameters.push(parameter)
      }
    }

    const sectionIndex = product.sections.findIndex(
      (item) => item.type === type
    )

    if (replace && sectionIndex >= 0) {
      product.sections.splice(sectionIndex, 1, newSection)
    } else if (replace && sectionIndex < 0) {
      product.sections.push(newSection)
    } else {
      product.sections.push(newSection)
    }

    return newSection
  }

  addProduct(): ProductSchema {
    const product: ProductSchema = {
      id: createUuid(),
      sections: [],
    }

    for (const param of this.machineSchema.parameters.slice(0, 1)) {
      this._addSection({
        product: product,
        sectionSchema: param,
        type: param.type,
        market: MarketType.Domestic,
        replace: false,
      })
    }

    this.schema.products.push(product)

    return product
  }

  deleteProduct(productId: string) {
    this.schema.products = this.schema.products.filter(
      (product) => product.id !== productId
    )
  }

  deleteSection(productId: string, sectionId: string) {
    const product = this.schema.products.find(
      (product) => product.id === productId
    )
    if (!product) {
      return
    }

    product.sections = product.sections.filter((section) => {
      return section.id !== sectionId
    })
  }
}

function addOptionsToParam(
  param: PackagingParamWidgetSchema,
  schema: ParamListItem,
  market: string
) {
  if (market === MarketType.Domestic) {
    if (schema.inputTypeDomestic === ParamSchemaType.RADIO) {
      const radioParam = param as RadioParamSchema
      radioParam.options = []

      if (schema.possibleValues) {
        radioParam.options = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
          }
        })
      }
    } else if (schema.inputTypeDomestic === ParamSchemaType.DROPDOWN) {
      const dropdownParam = param as DropdownParamSchema
      dropdownParam.options = []

      if (schema.possibleValues) {
        dropdownParam.options = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
          }
        })
      }
    } else if (schema.inputTypeDomestic === ParamSchemaType.CHOICE) {
      const choiceParam = param as ParamSchema
      choiceParam.choices = []

      if (schema.possibleValues) {
        choiceParam.choices = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
              isCorrect: schema.correctValue === possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
            isCorrect: schema.correctValue === possible.name,
          }
        })
      }
    }
  } else if (market === MarketType.Export) {
    if (schema.inputTypeExport === ParamSchemaType.RADIO) {
      const radioParam = param as RadioParamSchema
      radioParam.options = []

      if (schema.possibleValues) {
        radioParam.options = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
          }
        })
      }
    } else if (schema.inputTypeExport === ParamSchemaType.DROPDOWN) {
      const dropdownParam = param as DropdownParamSchema
      dropdownParam.options = []

      if (schema.possibleValues) {
        dropdownParam.options = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
          }
        })
      }
    } else if (schema.inputTypeDomestic === ParamSchemaType.CHOICE) {
      const choiceParam = param as ParamSchema
      choiceParam.choices = []

      if (schema.possibleValues) {
        choiceParam.choices = schema.possibleValues.map((possible) => {
          if (typeof possible === "string") {
            return {
              id: possible,
              label: possible,
              value: possible,
              isCorrect: schema.correctValue === possible,
            }
          }

          return {
            id: possible.id,
            label: possible.name,
            value: possible.id,
            isCorrect: schema.correctValue === possible.name,
          }
        })
      }
    }
  }
}

export interface UsePackagingFormProps {
  schema: MachineFormSchema | null
  deptId: string
}

export function usePackagingForm(props: UsePackagingFormProps) {
  const form = useForm()

  interface BuildFormParams {
    schema: MachineFormSchema
    deptId: string
    defaultValues?: Record<string, any>
  }

  function init(params: BuildFormParams) {
    form.unregister({ keepHandlers: true })
    form.reset({
      defaultValues: params.defaultValues,
    })

    form.register("id", {
      value: params.schema.id,
      shouldTrigger: false,
    })
    form.register("deptId", {
      value: params.deptId,
      shouldTrigger: false,
    })
    form.register("products", {
      value: params.defaultValues?.products || [],
      shouldTrigger: false,
    })

    for (const [index, product] of params.schema.products.entries()) {
      setProduct(index, product, params.defaultValues)
    }
  }

  function setProduct(
    index: number,
    schema: ProductSchema,
    defaultValues?: any
  ) {
    const productPath = `products[${index}]`
    form.setValue(`${productPath}.id`, schema?.id, { shouldTrigger: false })

    form.setValue(
      `${productPath}.sections[0]`,
      get(defaultValues, `${productPath}.sections[0]`, {
        id: schema.sections.at(0)?.id,
        type: schema.sections.at(0)?.type,
        parameters: get(
          defaultValues,
          `${productPath}.sections[0].parameters`,
          []
        ),
        status: FormParamStatus.DRAFT,
      }),
      { shouldTrigger: false }
    )
    // Set the market type to domestic by default
    form.setValue(
      `${productPath}.sections[0].parameters[0]`,
      get(defaultValues, `${productPath}.sections[0].parameters[0]`, {
        id: schema.sections.at(0)?.parameters.at(0)?.id,
        value: MarketType.Domestic,
        recordedAt: new Date().toISOString(),
      }),
      { shouldTrigger: false }
    )

    for (const [sectionIdx, sectionSchema] of schema.sections.entries()) {
      const sectionPath = `${productPath}.sections[${sectionIdx}]`
      setSection(sectionPath, sectionSchema, defaultValues)
    }
  }

  function setSection(
    path: string,
    schema: ProductSectionSchema,
    defaultValues?: any
  ) {
    if (schema.type === PackagingParamSchemaType.GENERAL_INFORMATION) {
      form.register(path, {
        shouldTrigger: false,
        value: get(defaultValues, path, {
          id: schema?.id,
          type: schema.type,
          status: FormParamStatus.DRAFT,
          parameters: [],
        }),
        validate: createGeneralSectionValidator({ path }),
      })
    } else {
      form.register(path, {
        shouldTrigger: false,
        value: get(defaultValues, path, {
          id: schema?.id,
          type: schema.type,
          status: FormParamStatus.DRAFT,
          parameters: [],
        }),
      })
    }

    for (const [paramIdx, param] of schema.parameters.entries()) {
      const paramPath = `${path}.parameters[${paramIdx}]`
      const limitSchema = param as ParamSchema
      const value = get(defaultValues, paramPath)

      if (schema.type === PackagingParamSchemaType.GENERAL_INFORMATION) {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
            value: value?.value || "",
            recordedAt: value?.recordedAt || new Date().toISOString(),
          },
        })

        continue
      }

      if (limitSchema.name === "Leakage Type") {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
            value: value?.value || [],
            recordedAt: value?.recordedAt || new Date().toISOString(),
          },
          validate: createLeakageValidator({
            path: paramPath,
            required: false,
            depends: `${path}.parameters[1]`,
          }),
        })
      } else if (limitSchema.validationType === ParamValidationType.LIMIT) {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
          },
          validate: createLimitValidator({
            schema: limitSchema,
            path: paramPath,
            form: form,
            required: true,
          }),
        })
      } else if (limitSchema.validationType === ParamValidationType.CHOICE) {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
          },
          validate: createChoiceValidator({
            schema: limitSchema,
            path: paramPath,
            form: form,
            required: true,
          }),
        })
      } else if (limitSchema.validationType === ParamValidationType.FLAG) {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
          },
          validate: createLeakedPacketsValidator({
            path: paramPath,
            depends: "",
            required: true,
          }),
        })
      } else {
        form.register(paramPath, {
          shouldTrigger: false,
          value: {
            id: param.id,
            ...value,
          },
          validate: createValidator(
            y.object({
              value: y.mixed().test({
                name: "test-packaging-param",
                test: function (value, ctx) {
                  if (value) {
                    return true
                  }

                  return ctx.createError({
                    message: `${paramPath} parameter is required`,
                  })
                },
              }),
            })
          ),
        })
      }
    }
  }

  useEffect(() => {
    if (props.schema) {
      init({
        schema: props.schema,
        deptId: props.deptId,
      })
    }
  }, [])

  return {
    form,
    rebuildForm: init,
    setProduct,
    setSection,
  }
}

export type PackagingFormContextValue = ReturnType<typeof usePackagingForm>

export function usePackagingFormContext(): PackagingFormContextValue {
  return useFormContext() as unknown as PackagingFormContextValue
}

interface AddSectionParams {
  product: ProductSchema
  sectionSchema: PackagingParamSchema
  type: PackagingParamSchemaType
  replace?: boolean
  market: MarketType
}

export interface MachineFormData {
  id: string
  deptId: string
  products: ProductFormData[]
}

export interface ProductFormData {
  id: string
  sections: ProductSectionFormData[]
}

export interface ProductSectionFormData {
  id: string
  type: PackagingParamSchemaType
  status?: FormParamStatus
  parameters: ParamFormData[]
}

export interface ParamFormData {
  id: string
  value: string
  recordedAt: string
  status?: FormParamStatus
  remark?: string
}
